import React, { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Col, Container, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input, Label, Media, Row, UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical } from "react-feather";
import { FaCheck, FaTimes, FaBan, FaEye } from "react-icons/fa";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Spinner } from "../../../../AbstractElements";
import CommonModal from "../../../UiKits/Modals/common/modal";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import BusinessApplicationModal from "./BusinessApplicationModal";

const STATUS_COLORS = {
  pending: { bg: "#fff8e1", text: "#f59e0b", label: "Pending" },
  approved: { bg: "#e8f5e9", text: "#16a34a", label: "Approved" },
  rejected: { bg: "#fce4ec", text: "#dc2626", label: "Rejected" },
  suspended: { bg: "#f3e5f5", text: "#7c3aed", label: "Suspended" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span
      style={{
        background: s.bg,
        color: s.text,
        padding: "3px 10px",
        borderRadius: "999px",
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    >
      {s.label}
    </span>
  );
};

const STATUS_TABS = ["all", "pending", "approved", "rejected", "suspended"];

const B2BApplicationsTable = () => {
  const getToken = () => JSON.parse(localStorage.getItem("token"));

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // ── View modal ────────────────────────────────────────────────────────
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  // ── Reject modal ──────────────────────────────────────────────────────
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // ─────────────────────────────────────────────────────────────────────
  // API
  // ─────────────────────────────────────────────────────────────────────
  const fetchApplications = async (page = 1, limit = perPage, status = activeTab) => {
    setLoading(true);
    try {
      const statusParam = status === "all" ? "" : `&status=${status}`;
      const res = await axios.get(
        `${baseURL}/api/business/applications?page=${page}&limit=${limit}${statusParam}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      console.log(res)
      setData(res.data.data || []);
      setTotalRows(res.data.count || res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(1, perPage, activeTab);
  }, [activeTab]);

  const handlePageChange = (page) => fetchApplications(page, perPage, activeTab);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchApplications(page, newPerPage, activeTab);
  };

  const handleSave = async (id, formData) => {
    await axios.put(
      `${baseURL}/api/business/applications/${id}`,
      formData,
      { headers: { Authorization: `${getToken()}` } }
    );
    fetchApplications(1, perPage, activeTab);
    Swal.fire({ title: "Application updated", icon: "success", confirmButtonColor: "#d3178a" });
  };

  const handleEditSubmit = async (id, formData) => {
    try {
      await axios.put(
        `${baseURL}/api/business/applications/${id}`,
        formData,
        { headers: { Authorization: `${getToken()}` } }
      );
      fetchApplications(1, perPage, activeTab);
      Swal.fire({ title: "Application updated", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ── Approve ───────────────────────────────────────────────────────────
  const handleApprove = async (row) => {
    const result = await Swal.fire({
      title: `Approve ${row.legal_name}?`,
      text: "This will give the business full wholesale access.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#9ca3af",
      confirmButtonText: "Yes, approve",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(
        `${baseURL}/api/business/applications/${row._id}/approve`,
        {},
        { headers: { Authorization: `${getToken()}` } }
      );
      fetchApplications(1, perPage, activeTab);
      Swal.fire({ title: "Approved!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────
  const openRejectModal = (row) => {
    setRejectTarget(row);
    setRejectReason("");
    setRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      Swal.fire({ title: "Please enter a rejection reason", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    try {
      await axios.post(
        `${baseURL}/api/business/applications/${rejectTarget._id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `${getToken()}` } }
      );
      setRejectModal(false);
      fetchApplications(1, perPage, activeTab);
      Swal.fire({ title: "Application rejected", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ── Suspend ───────────────────────────────────────────────────────────
  const handleSuspend = async (row) => {
    const { value: reason } = await Swal.fire({
      title: `Suspend ${row.legal_name}?`,
      input: "textarea",
      inputLabel: "Reason (optional)",
      inputPlaceholder: "e.g. Licence expired",
      showCancelButton: true,
      confirmButtonColor: "#7c3aed",
      cancelButtonColor: "#9ca3af",
      confirmButtonText: "Suspend",
    });
    if (reason === undefined) return; // cancelled
    try {
      await axios.post(
        `${baseURL}/api/business/applications/${row._id}/suspend`,
        { reason: reason || "Account suspended by admin" },
        { headers: { Authorization: `${getToken()}` } }
      );
      fetchApplications(1, perPage, activeTab);
      Swal.fire({ title: "Account suspended", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Columns
  // ─────────────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Business Name",
      selector: (row) => row.legal_name,
      sortable: true,
      cell: (row) => (
        <span className="font-semibold text-[#1D2433]">{row.legal_name}</span>
      ),
    },
    {
      name: "ABN",
      selector: (row) => row.abn,
      center: true,
      cell: (row) => (
        <span className="font-mono text-sm">{row.abn}</span>
      ),
    },
    {
      name: "Type",
      selector: (row) => row.business_type,
      sortable: true,
      center: true,
      cell: (row) => (
        <span className="text-xs">{row.business_type || "—"}</span>
      ),
    },
    {
      name: "Contact",
      center: true,
      cell: (row) => (
        <div className="text-xs text-center">
          <p className="mb-0 font-medium">{row.contact_name}</p>
          <p className="mb-0 text-[#1D243399]">{row.contact_phone}</p>
        </div>
      ),
    },
    {
      name: "Phone (User)",
      center: true,
      cell: (row) => (
        <span className="text-xs text-[#1D243399]">
          {row.user_id?.phoneNumber || "—"}
        </span>
      ),
    },
    {
      name: "Licence Expiry",
      selector: (row) => row.licence_expiry,
      sortable: true,
      center: true,
      cell: (row) => {
        const expiry = row.licence_expiry ? new Date(row.licence_expiry) : null;
        const isExpired = expiry && expiry < new Date();
        const isSoon = expiry && !isExpired &&
          (expiry - new Date()) / (1000 * 60 * 60 * 24) <= 30;
        return (
          <span
            className="text-xs font-medium"
            style={{ color: isExpired ? "#dc2626" : isSoon ? "#f59e0b" : "#16a34a" }}
          >
            {expiry ? moment(expiry).format("DD/MM/YYYY") : "—"}
            {isExpired && " ⚠️"}
            {isSoon && !isExpired && " ⏰"}
          </span>
        );
      },
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      center: true,
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      name: "Applied",
      selector: (row) => row.createdAt,
      sortable: true,
      center: true,
      cell: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              onClick={() => { setViewData(row); setViewModal(true); }}
            >
              View/Edit Details <FaEye className="ms-1" />
            </DropdownItem>

            {(row.status === "pending" || row.status === "rejected") && (
              <DropdownItem onClick={() => handleApprove(row)}>
                <span style={{ color: "#16a34a" }}>
                  Approve <FaCheck className="ms-1" />
                </span>
              </DropdownItem>
            )}
            {row.status === "pending" && (
              <DropdownItem onClick={() => openRejectModal(row)}>
                <span style={{ color: "#dc2626" }}>
                  Reject <FaTimes className="ms-1" />
                </span>
              </DropdownItem>
            )}
            {row.status === "approved" && (
              <DropdownItem onClick={() => handleSuspend(row)}>
                <span style={{ color: "#7c3aed" }}>
                  Suspend <FaBan className="ms-1" />
                </span>
              </DropdownItem>
            )}

            {row.status === "suspended" && (
              <DropdownItem onClick={() => handleApprove(row)}>
                <span style={{ color: "#16a34a" }}>
                  Re-activate <FaCheck className="ms-1" />
                </span>
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────
  // Filter
  // ─────────────────────────────────────────────────────────────────────
  const filtered = data.filter((it) =>
    `${it.legal_name} ${it.abn} ${it.contact_name} ${it.contact_phone} ${it.user_id?.phoneNumber}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Row xxl={12} className="pb-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-bolder">B2B Applications</h5>
          <div className="file-content">
            <Media className="align-items-center gap-2">
              <div className="mb-0 form-group position-relative search_outer d-flex align-items-center">
                <i className="fa fa-search"></i>
                <input
                  className="form-control border-0"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type="text"
                  placeholder="Search name, ABN, phone..."
                />
              </div>
            </Media>
          </div>
        </div>
      </Row>

      {/* ── Status tabs ──────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "5px 16px",
              borderRadius: "999px",
              border: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              cursor: "pointer",
              background: activeTab === tab ? "#D3178A" : "#f3f4f6",
              color: activeTab === tab ? "#fff" : "#374151",
              textTransform: "capitalize",
            }}
          >
            {tab === "all" ? "All" : STATUS_COLORS[tab]?.label}
          </button>
        ))}
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      {data.length !== 0 || loading ? (
        <DataTable
          data={filtered}
          columns={columns}
          pagination
          paginationServer
          progressPending={loading}
          progressComponent={<Loader />}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={
            <p className="py-5 text-center text-[#1D243399]">
              No {activeTab === "all" ? "" : activeTab} applications found.
            </p>
          }
        />
      ) : (
        <Col xxl="12" className="flex justify-center items-center py-5">
          {loading ? (
            <div className="loader-box"><Spinner /></div>
          ) : (
            <p className="text-center text-[#1D243399]">
              No {activeTab === "all" ? "" : activeTab} applications found.
            </p>
          )}
        </Col>
      )}

      {/* ══════════════════════════════════════════════════════════════
          VIEW DETAILS MODAL
      ══════════════════════════════════════════════════════════════ */}

      <BusinessApplicationModal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        data={viewData}
        onApprove={handleApprove}
        onReject={openRejectModal}
        onSuspend={handleSuspend}
        onSave={handleSave}
      />

      {/* ══════════════════════════════════════════════════════════════
          REJECT REASON MODAL
      ══════════════════════════════════════════════════════════════ */}
      <CommonModal
        isOpen={rejectModal}
        title="Reject Application"
        toggler={() => setRejectModal(false)}
        size="md"
      >
        <Container>
          <p className="text-sm text-[#1D243399] mb-3">
            Please provide a reason — this will be shown to the business so they know what to fix.
          </p>
          <FormGroup>
            <Label>Rejection Reason <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Licence number could not be verified. Please resubmit with a clear copy."
            />
          </FormGroup>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => setRejectModal(false)}
              style={{
                background: "#f3f4f6", color: "#374151",
                border: "none", borderRadius: "12px",
                padding: "8px 20px", fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectSubmit}
              style={{
                background: "#dc2626", color: "#fff",
                border: "none", borderRadius: "12px",
                padding: "8px 20px", fontWeight: 600, cursor: "pointer",
              }}
            >
              Confirm Reject
            </button>
          </div>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

// ── Small helper component ────────────────────────────────────────────────────
const DetailRow = ({ label, value, valueStyle = {} }) => (
  <Col xxl={6} md={6} className="mb-2">
    <p className="mb-0 text-xs text-[#1D243399]">{label}</p>
    <p className="mb-0 text-sm font-medium text-[#1D2433]" style={valueStyle}>
      {value || "—"}
    </p>
  </Col>
);

export default B2BApplicationsTable;